import {
	deleteSharedPaymentMethodConfirmation,
	describePaymentMethodSharingDependent,
	paymentMethodSharingDependentsUnavailable,
	stopSharingPaymentMethodConfirmation,
} from '@supply-mover-2-0/core';
import type { PaymentMethodSharingDependentDto } from '@supply-mover-2-0/shared';
import React from 'react';
import { useTranslation } from '../../providers/TranslationProvider';
import { cn } from '../../utils/cn';
import { ConfirmDialog } from '../dialogs/ConfirmDialog';

interface PaymentMethodSharingDependentsListProps {
	dependents: Array<PaymentMethodSharingDependentDto>;
	/**
	 * Name the payment method on each line. For lists that span methods (an
	 * ownership change); a dialog about one method leaves it off.
	 */
	showPaymentMethod?: boolean;
	className?: string;
}

/**
 * PD-17009 — what on other accounts depends on a shared payment method, one
 * line per account and kind: "Vietnam Noodle Restaurant: 2 payment schedules
 * (Weekly, Supplies)".
 *
 * Shared by every confirmation that takes a shared method away from another
 * account (stop sharing, delete, a customer leaving the ownership group), in
 * the CRM and on the storefront, so they all list the same things the same
 * way. Renders nothing for an empty list.
 */
export function PaymentMethodSharingDependentsList({
	dependents,
	showPaymentMethod = false,
	className,
}: PaymentMethodSharingDependentsListProps): React.ReactElement | null {
	const { t } = useTranslation();

	if (dependents.length === 0) {
		return null;
	}

	return (
		<ul
			className={cn(
				'max-h-48 list-disc space-y-1 overflow-y-auto pl-5 text-sm text-default',
				className,
			)}
		>
			{dependents.map((dependent) => (
				<li
					key={`${dependent.paymentMethodId}:${dependent.dependentClientId}:${dependent.kind}`}
				>
					<span className="font-medium">
						{t(dependent.dependentClientName ?? 'Another account')}
					</span>
					{': '}
					{describePaymentMethodSharingDependent(dependent, t)}
					{showPaymentMethod ? (
						<span className="text-muted">
							{' · '}
							{t(dependent.paymentMethodLabel)}
							{dependent.ownerClientName != null &&
							dependent.ownerClientName !== ''
								? ` (${t(dependent.ownerClientName)})`
								: ''}
						</span>
					) : null}
				</li>
			))}
		</ul>
	);
}

interface PaymentMethodStopSharingDialogProps {
	isOpen: boolean;
	/** Whether the method is being un-shared or deleted outright. */
	action: 'stopSharing' | 'delete';
	/**
	 * What depends on the method, or null when the lookup failed. The change
	 * is still allowed then, with a warning that says the check could not be
	 * made rather than one that says nothing depends on it.
	 */
	dependents: Array<PaymentMethodSharingDependentDto> | null;
	/** True while the dependents are being looked up; holds the confirm button. */
	isLoading?: boolean;
	/** True while the change itself is being saved. */
	isProcessing?: boolean;
	onConfirm: () => void;
	onCancel: () => void;
}

/**
 * PD-17009 — the confirmation shown before a shared payment method is taken
 * away from the accounts it is shared with: sharing turned off, or the method
 * deleted. It warns, lists what will stop working, and then lets the change
 * go ahead; nothing is blocked.
 */
export function PaymentMethodStopSharingDialog({
	isOpen,
	action,
	dependents,
	isLoading = false,
	isProcessing = false,
	onConfirm,
	onCancel,
}: PaymentMethodStopSharingDialogProps): React.ReactElement {
	const { t } = useTranslation();
	// While the lookup runs, word it as if something depends on the method:
	// the list is about to say what, and "nothing is using it" must never show
	// before that is known.
	const hasDependents =
		isLoading || dependents == null || dependents.length > 0;
	const confirmation =
		action === 'delete'
			? deleteSharedPaymentMethodConfirmation(t, hasDependents)
			: stopSharingPaymentMethodConfirmation(t, hasDependents);

	return (
		<ConfirmDialog
			isOpen={isOpen}
			title={confirmation.title}
			message={confirmation.message}
			confirmText={confirmation.confirmText}
			variant={action === 'delete' ? 'danger' : 'warning'}
			isProcessing={isProcessing}
			confirmDisabled={isLoading}
			onConfirm={onConfirm}
			onCancel={onCancel}
		>
			{isLoading ? (
				<p className="text-sm text-muted">
					{t('Checking what uses this payment method...')}
				</p>
			) : dependents == null ? (
				<p className="text-sm text-warning">
					{paymentMethodSharingDependentsUnavailable(t)}
				</p>
			) : (
				<PaymentMethodSharingDependentsList dependents={dependents} />
			)}
		</ConfirmDialog>
	);
}
